import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import qs from 'qs';
import { Form, FormInstance, FormErrors, TextInput } from 'blueprint-form';

import Meta from '../../atoms/meta';
import Button from '../../molecules/button';
import Card from '../../organisms/card';
import Callout from '../../organisms/callout';
import ErrorCallout from '../../organisms/callout/error-callout';
import PasswordInput from '../../organisms/password';
import Modal from '../../organisms/modal';

import {
  useRequestResetPassword,
  RequestResetParams
} from '../../../libs/user';
import { BaseResponse, PostJSON } from '../../../libs/request';

const RequestResetPage: React.FC = () => {
  const { data, isLoading, error, reset } = useRequestResetPassword();
  const initialData: RequestResetParams = {
    email: ''
  };

  const form = new FormInstance<RequestResetParams>(initialData);

  function submit(data: RequestResetParams) {
    if (data.email) {
      reset(data.email);
    }
    form.reset();
  }

  return (
    <Card>
      <Meta title="Reset Password" />
      <h1 className="text-sans-lg mb-7">Reset password</h1>
      <Form form={form} onSubmit={submit}>
        {error && <ErrorCallout className="mb-3">{error}</ErrorCallout>}
        <TextInput label="Email" name="email" type="email" required />
        <p className="mb-8">
          <Link className="text-brand hover-brand-text" to="/">
            Want to log in? Click here.
          </Link>
        </p>
        {data && data.status === 'OK' && (
          <Callout intent="success" className="mb-8">
            <p>
              If the account exists, a message containing a link to reset your
              password will be sent to your email address.
            </p>
            <p>
              Note that sometimes it can take up to half an hour for the email
              to appear in your inbox, and that the email might be sent to SPAM.
              Contact your account manager if you have any questions.
            </p>
          </Callout>
        )}
        <Button type="submit" intent="success" disabled={isLoading} fill>
          Request Reset
        </Button>
      </Form>
    </Card>
  );
};

interface ResetPasswordPageProps {
  email: string;
  token: string;
}

interface ResetPasswordFormData {
  email: string;
  password: string;
  confirm: string;
}

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({
  email,
  token
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);

  const initialData: ResetPasswordFormData = {
    email,
    password: '',
    confirm: ''
  };

  const form = new FormInstance<ResetPasswordFormData>(initialData);

  const validate = (values: ResetPasswordFormData): FormErrors => {
    const errors: FormErrors = {};
    if (values.password !== values.confirm) {
      errors.confirm = 'The two passwords must match!';
    }
    return errors;
  };

  function submit(data: ResetPasswordFormData) {
    if (!data.password) {
      return;
    }
    setIsUpdating(true);
    PostJSON<BaseResponse>({
      url: '/user/reset_password/',
      params: {
        email,
        token,
        password: data.password
      }
    }).then((response) => {
      setIsUpdating(false);
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Resetting Password',
          content: response.message
        });
        return;
      }
      setIsPasswordReset(true);
      form.getForm().resetForm();
      return;
    });
  }

  const reload = () => {
    window.location.assign('/');
  };

  return (
    <Card
      style={{
        marginBottom: '80px'
      }}
    >
      <Meta title="Reset Password" />
      <h1 className="text-sans-lg mb-7">Reset password</h1>
      <Form form={form} onSubmit={submit} validate={validate}>
        <TextInput label="Email" name="email" disabled />
        <PasswordInput
          label="Password"
          name="password"
          showStrength
          enforceStrength
          required
        />
        <TextInput
          label="Confirm Password"
          name="confirm"
          type="password"
          required
        />
        {isPasswordReset === true && (
          <Callout intent="success" className="mb-8">
            <Button
              onClick={reload}
              bare
              variant="minimal"
              className="underline"
            >
              Password successfully updated, click here to log in!
            </Button>
          </Callout>
        )}
        <Button type="submit" intent="success" disabled={isUpdating} fill>
          Update Password
        </Button>
      </Form>
    </Card>
  );
};

const ResetPage: React.FC = () => {
  const { search } = useLocation();
  const query = qs.parse(search, {
    ignoreQueryPrefix: true
  });
  const token = query && query.token ? (query.token as string) : null;
  const email = query && query.email ? (query.email as string) : null;

  if (token && email) {
    return <ResetPasswordPage email={email} token={token} />;
  }

  return <RequestResetPage />;
};

export default ResetPage;
